import type { Patient } from "@medplum/fhirtypes";

/**
 * Canonical DTOs for the mobile member-profile editor flow.
 * Web endpoints and the mobile client both import these shapes to avoid contract drift.
 */
export interface InsuranceFormValues {
  payorName: string;
  payorCode: string;
  planName: string;
  policyNumber: string;
  groupNumber: string;
  startDate: string;
  endDate: string;
}

export const EMPTY_INSURANCE_FORM_VALUES: InsuranceFormValues = {
  payorName: "",
  payorCode: "",
  planName: "",
  policyNumber: "",
  groupNumber: "",
  startDate: "",
  endDate: "",
};

export interface MemberProfilePatientDetails {
  name: string;
  preferredName: string;
  gender: string;
  dateOfBirth: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateTwoLetter: string;
  zipCode: string;
  email: string;
  geneticBackground: string;
}

export interface SaveMemberProfileInsuranceState {
  primary: InsuranceFormValues;
  secondary: InsuranceFormValues;
  hasSecondary: boolean;
}

export interface MemberProfileInsuranceState extends SaveMemberProfileInsuranceState {
  primaryInsuranceId?: string;
  secondaryInsuranceId?: string;
}

export interface MemberProfileEditorData {
  patientDetails: MemberProfilePatientDetails;
  insurance: MemberProfileInsuranceState;
  emailChangeEnabled: boolean;
}

export interface SaveMemberProfileRequest {
  patientDetails: Omit<MemberProfilePatientDetails, "email">;
  insurance: SaveMemberProfileInsuranceState;
}

export interface SaveMemberProfileResponse {
  data: MemberProfileEditorData;
  profile: Patient;
}

/**
 * The only values the profile editor's Race and Ethnicity selector recognizes.
 * The underlying FHIR field (GeneticBackgroundObservation, LOINC 46463-6) is a
 * comma-delimited string shared with the genetic-background document parser, which
 * can write free-text clinical prose into it. Treat this list as the source of truth
 * for what counts as a real selection so parser prose never surfaces as profile data.
 */
export const RACE_ETHNICITY_OPTIONS: readonly string[] = [
  "American Indian or Alaska Native",
  "Asian",
  "Black or African American",
  "Hispanic or Latino",
  "Middle Eastern or North African",
  "Native Hawaiian or Other Pacific Islander",
  "White",
];

const LEGACY_RACE_ETHNICITY_SELECTIONS: Record<string, string> = {
  Caucasian: "White",
};

/**
 * Maps retired provider-form values to the canonical machine values without altering
 * unrelated comma-delimited content written by older forms or document parsers.
 */
export function normalizeLegacyRaceEthnicitySelection(value: string | undefined): string {
  if (!value?.length) return "";

  const selections = value.split(",");
  return selections
    .map((selection) => LEGACY_RACE_ETHNICITY_SELECTIONS[selection] ?? selection)
    .join(",");
}

/**
 * Keeps only recognized race/ethnicity selections from the shared comma-delimited
 * genetic-background string, dropping parser prose and duplicates while preserving order.
 * Selections are exact, case-sensitive matches — these are fixed machine values, not free text.
 */
export function filterToAllowedRaceEthnicity(value: string | undefined): string {
  if (!value?.length) return "";

  const allowed: string[] = [];
  for (const token of value.split(",")) {
    const trimmed = token.trim();
    if (RACE_ETHNICITY_OPTIONS.includes(trimmed) && !allowed.includes(trimmed)) {
      allowed.push(trimmed);
    }
  }

  return allowed.join(", ");
}
